// src/services/adminApi.ts
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AllUsersRes } from "../../components/Admin/CustomerProfilesCard";
import { base } from "../../constants/apis";
import CookieService from "../../context/cookies";
import { ApiResponse } from "../../types/ApiResponse";
import {
  PartnerCompanyDetails,
  ServicesData,
  LogisticsRequestQuote,
} from "../../types/PartnerCompanyDetails";
import { FullRegistrationFormType as User } from "../../schemas/FullRegistrationFormSchema";

type CustomerWithQuote = Pick<
  User,
  "uid" | "company_name" | "work_email" | "first_name" | "last_name"
> & {
  logistic_ids: number[];
  total_count: number;
};

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis/admin`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("adminToken");

      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["usersProfiles", "rfqs"],
  endpoints: (builder) => ({
    getAllUsers: builder.query<
      AllUsersRes,
      { page: number; search?: string; limit?: number }
    >({
      query: ({ page, search, limit }) => ({
        url: `/users/get.php?page=${page}&search=${search || ""}&limit=${
          limit || 10
        }`,
        method: "POST",
      }),
      transformResponse: (res: ApiResponse<AllUsersRes>) => res.data,
      providesTags: ["usersProfiles"],
    }),

    onboardClient: builder.mutation({
      query: (payload) => ({
        url: "/users/user_onboard.php",
        method: "POST",
        body: payload,
      }),

      invalidatesTags: ["usersProfiles"],
    }),

    getAllPartnerCompanyWithServices: builder.query<
      ApiResponse<(PartnerCompanyDetails & { services_data: ServicesData })[]>,
      { uid: number }
    >({
      query: (payload) => ({
        url: "/partner_company_details/get.php",
        method: "POST",
        body: payload,
      }),

      providesTags: ["rfqs"],
    }),

    getCustomersWithQuotes: builder.query({
      query: (payload) => ({
        url: "/logistics_request_quotes/get_status_wise_data.php",
        method: "POST",
        body: payload,
      }),

      transformResponse: (res: ApiResponse<CustomerWithQuote[]>) => {
        if (res.status === "success") return res.data;

        return [];
      },
    }),

    getAllRFQ: builder.query<LogisticsRequestQuote[], { uid: number }>({
      query: (payload) => ({
        url: "/logistics_request_quotes/get.php",
        method: "POST",
        body: payload,
      }),

      providesTags: ["rfqs"],
      transformResponse: (
        res: ApiResponse<LogisticsRequestQuote[]> & {
          trade_flow_quotes: LogisticsRequestQuote[];
        },
      ) => {
        if (res.status === "fail") {
          return [];
        }

        // const allQuotes = res.data.concat(res.trade_flow_quotes);
        const allQuotes = res.data;

        return allQuotes.sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : null;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : null;

          // both missing → keep relative order
          if (!aTime && !bTime) return 0;

          // only a missing → a goes below
          if (!aTime) return 1;

          // only b missing → b goes below
          if (!bTime) return -1;

          // latest first
          return bTime - aTime;
        });
      },
    }),

    updateRFQs: builder.mutation({
      query: (payload) => ({
        url: "/logistics_request_quotes/update.php",
        method: "POST",
        body: payload,
      }),

      invalidatesTags: ["rfqs"],
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useOnboardClientMutation,
  useGetAllPartnerCompanyWithServicesQuery,
  useGetAllRFQQuery,
  useUpdateRFQsMutation,

  useGetCustomersWithQuotesQuery,
} = adminApi;
